import { put, call, all, select, takeLatest } from 'redux-saga/effects';
import {
    GET_SAVED_FILTERS_REQUEST,
    getSavedFiltersSuccess,
    getSavedFiltersFailure,
    SAVE_FILTER_REQUEST,
    saveFilterSuccess,
    saveFilterFailure,
    DELETE_FILTER_REQUEST,
    deleteFilterSuccess,
    deleteFilterFailure,
    SET_DEFAULT_FILTER_REQUEST,
    setDefaultFilterSuccess,
    setDefaultFilterFailure
} from '../actions/savedFilters';
import { getUserSettingsScreen, putUserSettingsScreen } from '../services/savedFilters';
import { showGlobalError } from '../../app/actions/app';
import { accessTokenSelector } from '../../auth/selectors/token';
import { SAVED_FILTERS_SCREENS } from '../reducers/savedFilters';
import { savedFiltersScreenSelector } from '../selectors/savedFilters';

const getSavedFiltersWorker = function* () {
    try {
        const token = yield select(accessTokenSelector);

        // NOTE: the API is now per-screen (GET /api/v1/user-settings/:screen), so we fetch
        // every known screen in parallel. It returns a bare `null` body when nothing has
        // been saved yet for that screen.
        const responses = yield all(SAVED_FILTERS_SCREENS.map(screen => call(getUserSettingsScreen, screen, token)));

        const screens = SAVED_FILTERS_SCREENS.reduce((acc, screen, index) => {
            acc[screen] = responses[index].data?.filters || {};
            return acc;
        }, {});

        yield put(getSavedFiltersSuccess(screens));
    } catch (err) {
        yield put(getSavedFiltersFailure(err));
    }
};

const saveFilterWorker = function* ({ payload: { screen, name, values } }) {
    try {
        const token = yield select(accessTokenSelector);
        const { presets, defaultPreset } = yield select(savedFiltersScreenSelector(screen));

        const nextPresets = [
            ...presets.filter(preset => preset.name !== name),
            { name, values }
        ];

        yield call(putUserSettingsScreen, screen, { presets: nextPresets, defaultPreset }, token);

        yield put(saveFilterSuccess(screen, nextPresets));
        yield put(showGlobalError(true, 200, 'created'));
    } catch (err) {
        yield put(saveFilterFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const deleteFilterWorker = function* ({ payload: { screen, name } }) {
    try {
        const token = yield select(accessTokenSelector);
        const { presets, defaultPreset } = yield select(savedFiltersScreenSelector(screen));

        const nextPresets = presets.filter(preset => preset.name !== name);
        const nextDefault = defaultPreset === name ? null : defaultPreset;

        yield call(putUserSettingsScreen, screen, { presets: nextPresets, defaultPreset: nextDefault }, token);

        yield put(deleteFilterSuccess(screen, nextPresets, nextDefault));
    } catch (err) {
        yield put(deleteFilterFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const setDefaultFilterWorker = function* ({ payload: { screen, name } }) {
    try {
        const token = yield select(accessTokenSelector);
        const { presets, defaultPreset } = yield select(savedFiltersScreenSelector(screen));

        const nextDefault = defaultPreset === name ? null : name;

        yield call(putUserSettingsScreen, screen, { presets, defaultPreset: nextDefault }, token);

        yield put(setDefaultFilterSuccess(screen, nextDefault));
    } catch (err) {
        yield put(setDefaultFilterFailure(err));
        yield put(showGlobalError(true, err));
    }
};

export default [
    takeLatest(GET_SAVED_FILTERS_REQUEST, getSavedFiltersWorker),
    takeLatest(SAVE_FILTER_REQUEST, saveFilterWorker),
    takeLatest(DELETE_FILTER_REQUEST, deleteFilterWorker),
    takeLatest(SET_DEFAULT_FILTER_REQUEST, setDefaultFilterWorker)
];
